(function () {
  const STORAGE_KEY = "collection-filters-sidebar-open";
  const MOBILE_MQ = window.matchMedia("(max-width: 63.99em)");

  function isMobile() {
    return MOBILE_MQ.matches;
  }

  function debounce(fn, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class CollectionFiltersLayout extends HTMLElement {
    constructor() {
      super();
      this._selectors = {
        toggle: "[js-toggle-filters]",
        toggleLabel: "[js-toggle-filters-label]",
        toggleCount: "[js-toggle-filters-count]",
        activeFilters: "active-filters",
      };
      this._labels = {
        hide: this.dataset.labelHide || "Hide Filters",
        show: this.dataset.labelShow || "Show Filters",
        filters: this.dataset.labelFilters || "Filters",
      };
    }

    connectedCallback() {
      this._toggle = this.querySelector(this._selectors.toggle);
      this._toggleLabel = this.querySelector(this._selectors.toggleLabel);
      this._toggleCount = this.querySelector(this._selectors.toggleCount);
      this._main = this.querySelector(".collection-filters-layout__main");

      this._restoreSidebarState();
      this._setListeners();
      this._wireGridLoading();
      this._syncToggleCount();
      this._wireExternalClearAll();
      this._wirePriceRangeClear();

      if (!isMobile()) {
        const expanded = !this.classList.contains("collection-filters-layout--sidebar-hidden");
        this._updateToggleState(expanded);
      } else {
        this._updateToggleState(false);
      }
    }

    _restoreSidebarState() {
      if (isMobile()) return;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "false") {
          this.classList.add("collection-filters-layout--sidebar-hidden");
          this._updateToggleState(false);
        }
      } catch (e) { }
    }

    _setListeners() {
      this._toggle?.addEventListener("click", this._onToggleClick);
      document.addEventListener("filter:change", this._onFilterChange);
      document.addEventListener("filter:change", this._onFilterChangeComplete);
      MOBILE_MQ.addEventListener("change", this._onBreakpointChange);
      this._drawer = document.getElementById("CollectionFiltersDrawer");
      this._drawer?.addEventListener("close", this._onDrawerClose);
      this._drawer?.addEventListener("click", this._onDrawerRemoveFilter, true);
    }

    disconnectedCallback() {
      this._toggle?.removeEventListener("click", this._onToggleClick);
      document.removeEventListener("filter:change", this._onFilterChange);
      document.removeEventListener("filter:change", this._onFilterChangeComplete);
      MOBILE_MQ.removeEventListener("change", this._onBreakpointChange);
      this._drawer?.removeEventListener("close", this._onDrawerClose);
      this._drawer?.removeEventListener("click", this._onDrawerRemoveFilter, true);
    }

    _wireGridLoading() {
      const startLoading = () => this._setGridLoading(true);

      this.addEventListener(
        "input",
        (e) => {
          if (e.target.closest(".collection-filters [js-filter-form]")) startLoading();
        },
        true,
      );

      this.addEventListener(
        "click",
        (e) => {
          if (e.target.closest("[js-clear-all], [js-clear-price-range]")) startLoading();
        },
        true,
      );

      document.addEventListener("filter:clearAll", startLoading);
    }

    _setGridLoading(isLoading) {
      if (!this._main) return;
      this._main.classList.toggle("is-loading", isLoading);
    }

    _onFilterChangeComplete = () => {
      this._setGridLoading(false);
      this._resetDrawerScroll();
      this._unlockPageScroll();
    };

    _onDrawerRemoveFilter = (e) => {
      const btn = e.target.closest("[js-remove]");
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const form = this._drawer.querySelector('[data-scope="mobile"] [js-filter-form]');
      const input = [...(form?.querySelectorAll(`input[name="${CSS.escape(btn.dataset.name)}"]`) || [])].find(
        (el) => el.value === btn.dataset.value && !el.disabled,
      );
      input?.click();
    };

    _onDrawerClose = () => {
      const active = document.activeElement;
      if (this._drawer && active && this._drawer.contains(active)) {
        active.blur();
      }
      this._unlockPageScroll();
      requestAnimationFrame(() => this._unlockPageScroll());
    };

    _unlockPageScroll() {
      const scrolling = document.scrollingElement || document.documentElement;
      const maxScroll = Math.max(0, scrolling.scrollHeight - window.innerHeight);
      if (window.scrollY > maxScroll) {
        window.scrollTo(0, maxScroll);
      }
    }

    _resetDrawerScroll() {
      const drawer = document.getElementById("CollectionFiltersDrawer");
      if (!drawer) return;
      const reset = () => {
        drawer.scrollTop = 0;
      };
      reset();
      requestAnimationFrame(() => {
        reset();
        requestAnimationFrame(reset);
      });
      setTimeout(reset, 50);
    }

    _wireExternalClearAll() {
      document
        .querySelectorAll(".collection-filters-drawer__clear[js-clear-all], .collection-filters-toolbar__clear[js-clear-all]")
        .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent("filter:clearAll"));
        });
      });
    }

    _wirePriceRangeClear() {
      document.addEventListener("click", (e) => {
        const btn = e.target.closest("[js-clear-price-range]");
        if (!btn) return;
        e.preventDefault();
        document.querySelectorAll("price-range-slider").forEach((slider) => {
          slider.querySelectorAll("[js-range-input-min], [js-range-input-max]").forEach((input) => {
            input.value = "";
          });
          const minRange = slider.querySelector("[js-range-min]");
          const maxRange = slider.querySelector("[js-range-max]");
          const max = parseFloat(slider.dataset.rangeMax || "0");
          if (minRange) minRange.value = 0;
          if (maxRange) maxRange.value = max;
          slider.querySelector("[js-range-input-min]")?.dispatchEvent(new Event("input", { bubbles: true }));
        });
      });
    }

    _onBreakpointChange = () => {
      if (!isMobile()) {
        const hidden = this.classList.contains("collection-filters-layout--sidebar-hidden");
        this._updateToggleState(!hidden);
      } else {
        this._updateToggleState(false);
      }
    };

    _onToggleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isMobile()) {
        document.getElementById("CollectionFiltersDrawer")?.open();
        return;
      }

      const willShow = this.classList.contains("collection-filters-layout--sidebar-hidden");
      this.classList.toggle("collection-filters-layout--sidebar-hidden", !willShow);
      try {
        localStorage.setItem(STORAGE_KEY, willShow ? "true" : "false");
      } catch (err) { }
      this._updateToggleState(willShow);
    };

    _updateToggleState(expanded) {
      if (!this._toggle || !this._toggleLabel) return;
      if (isMobile()) {
        this._toggle.setAttribute("aria-expanded", "false");
        this._toggleLabel.textContent = this._labels.filters;
        return;
      }
      this._toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      this._toggleLabel.textContent = expanded ? this._labels.hide : this._labels.show;
    }

    _onFilterChange = (e) => {
      const doc = e.detail?.parsedHTML;
      if (!doc) return;
      this._syncToggleCount(doc);
    };

    _syncToggleCount(doc) {
      if (!this._toggleCount) return;
      const source = doc || document;
      const activeFilters = source.querySelector(this._selectors.activeFilters);
      const count = activeFilters ? activeFilters.querySelectorAll("[js-active-filter]").length : 0;
      if (count > 0) {
        this._toggleCount.textContent = `[${count}]`;
        this._toggleCount.classList.remove("hidden");
      } else {
        this._toggleCount.textContent = "";
        this._toggleCount.classList.add("hidden");
      }
    }
  }

  class CollectionResultsCount extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
    }

    connectedCallback() {
      this._textEl = this.querySelector("[js-results-count-text]");
      this._singular = this.dataset.labelSingular || "1 result";
      this._pluralTemplate = this.dataset.labelPlural || "{{ count }} results";
      document.addEventListener("filter:change", this._onFilterChange);
      this._fetchCount();
    }

    disconnectedCallback() {
      document.removeEventListener("filter:change", this._onFilterChange);
      this._abortController?.abort();
    }

    _onFilterChange = debounce(() => {
      this._fetchCount();
    }, 300);

    _buildCountUrl(page) {
      const base = this.dataset.countUrl || "";
      const params = new URLSearchParams(window.location.search);
      params.delete("page");
      if (page > 1) params.set("page", String(page));
      const qs = params.toString();
      const joiner = base.includes("?") ? "&" : "?";
      return qs ? `${base}${joiner}${qs}` : base;
    }

    async _fetchCount() {
      this._abortController?.abort();
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      try {
        const firstRes = await fetch(this._buildCountUrl(1), { signal });
        if (!firstRes.ok) throw new Error("Count fetch failed");
        const firstData = await firstRes.json();
        let total = firstData.visible || 0;
        const pages = firstData.pages || 1;

        if (pages > 1) {
          const fetches = [];
          for (let p = 2; p <= pages; p += 1) {
            fetches.push(
              fetch(this._buildCountUrl(p), { signal })
                .then((r) => r.json())
                .then((d) => d.visible || 0),
            );
          }
          const rest = await Promise.all(fetches);
          total += rest.reduce((sum, n) => sum + n, 0);
        }

        this._render(total);
      } catch (err) {
        if (err.name === "AbortError") return;
        const fallback = parseInt(this.dataset.fallbackCount || "0", 10);
        this._render(fallback);
      }
    }

    _render(count) {
      if (this._textEl) {
        if (count === 1) {
          this._textEl.textContent = this._singular;
        } else {
          this._textEl.textContent = this._pluralTemplate.replace(/\[count\]|\{\{\s*count\s*\}\}/g, String(count));
        }
      }

      document.querySelectorAll("[js-view-results-count]").forEach((el) => {
        const template = el.getAttribute("data-label-template");
        if (template) {
          el.textContent = template.replace(/\[count\]|\{\{\s*count\s*\}\}/g, String(count));
        } else {
          el.textContent = String(count);
        }
      });
    }
  }

  class PriceRangeSlider extends HTMLElement {
    connectedCallback() {
      this._minRange = this.querySelector("[js-range-min]");
      this._maxRange = this.querySelector("[js-range-max]");
      this._minInput = this.querySelector("[js-range-input-min]");
      this._maxInput = this.querySelector("[js-range-input-max]");
      this._activeTrack = this.querySelector("[js-range-active]");
      this._max = parseFloat(this.dataset.rangeMax || "0");

      this._minRange?.addEventListener("input", this._onRangeInput);
      this._maxRange?.addEventListener("input", this._onRangeInput);
      this._minInput?.addEventListener("change", this._onTextInput);
      this._maxInput?.addEventListener("change", this._onTextInput);

      this._updateUI();
    }

    disconnectedCallback() {
      this._minRange?.removeEventListener("input", this._onRangeInput);
      this._maxRange?.removeEventListener("input", this._onRangeInput);
      this._minInput?.removeEventListener("change", this._onTextInput);
      this._maxInput?.removeEventListener("change", this._onTextInput);
    }

    _onRangeInput = () => {
      let minVal = parseFloat(this._minRange.value);
      let maxVal = parseFloat(this._maxRange.value);
      if (minVal > maxVal) {
        if (this._minRange === document.activeElement) {
          maxVal = minVal;
          this._maxRange.value = maxVal;
        } else {
          minVal = maxVal;
          this._minRange.value = minVal;
        }
      }
      this._minInput.value = minVal > 0 ? minVal : "";
      this._maxInput.value = maxVal < this._max ? maxVal : "";
      this._updateUI();
      this._minInput.dispatchEvent(new Event("input", { bubbles: true }));
    };

    _onTextInput = () => {
      let minVal = parseFloat(this._minInput.value) || 0;
      let maxVal = parseFloat(this._maxInput.value) || this._max;
      if (minVal > maxVal) {
        if (document.activeElement === this._minInput) maxVal = minVal;
        else minVal = maxVal;
      }
      this._minRange.value = minVal;
      this._maxRange.value = maxVal;
      this._minInput.value = minVal > 0 ? minVal : "";
      this._maxInput.value = maxVal < this._max ? maxVal : "";
      this._updateUI();
      this._minInput.dispatchEvent(new Event("input", { bubbles: true }));
    };

    _updateUI() {
      if (!this._activeTrack || !this._max) return;
      const min = parseFloat(this._minRange?.value || 0);
      const max = parseFloat(this._maxRange?.value || this._max);
      const left = (min / this._max) * 100;
      const right = 100 - (max / this._max) * 100;
      this._activeTrack.style.left = `${left}%`;
      this._activeTrack.style.right = `${right}%`;
    }
  }

  class CollectionTypeFilterBadges extends HTMLElement {
    constructor() {
      super();
      this._sidebarFormSelector =
        '#CollectionFiltersSidebar filter-sort[data-scope="sidebar"] [js-filter-form]';
    }

    connectedCallback() {
      this.addEventListener("click", this._onClick);
      document.addEventListener("filter:change", this._onFilterChange);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._onClick);
      document.removeEventListener("filter:change", this._onFilterChange);
    }

    _getSidebarForm() {
      return document.querySelector(this._sidebarFormSelector);
    }

    _findMatchingInput(form, paramName, value) {
      if (!form) return null;
      const inputs = form.querySelectorAll(`input[name="${CSS.escape(paramName)}"]`);
      return Array.from(inputs).find((input) => input.value === value) || null;
    }

    _onClick = (e) => {
      const badge = e.target.closest("[js-type-filter-badge]");
      if (!badge || badge.disabled) return;

      const checkbox = this._findMatchingInput(
        this._getSidebarForm(),
        badge.dataset.paramName,
        badge.dataset.value,
      );
      if (checkbox && !checkbox.disabled) {
        checkbox.click();
      }
    };

    _onFilterChange = (e) => {
      const doc = e.detail?.parsedHTML;
      if (!doc) return;

      const source = doc.querySelector("collection-type-filter-badges");
      if (!source) return;

      this.innerHTML = source.innerHTML;
    };
  }

  class CollectionGridSort extends HTMLElement {
    constructor() {
      super();
      this._selectors = {
        button: "[js-collection-sort-button]",
        menu: "[js-collection-sort-menu]",
        option: ".collection-grid__sort-option",
        select: "[js-collection-sort-trigger]",
      };
    }

    connectedCallback() {
      this._button = this.querySelector(this._selectors.button);
      this._menu = this.querySelector(this._selectors.menu);
      this._select = this.querySelector(this._selectors.select);
      if (!this._button || !this._menu || !this._select) return;

      this._button.addEventListener("click", this._onButtonClick);
      this._menu.addEventListener("click", this._onMenuClick);
      document.addEventListener("click", this._onDocumentClick);
      document.addEventListener("keydown", this._onKeydown);
    }

    disconnectedCallback() {
      this._button?.removeEventListener("click", this._onButtonClick);
      this._menu?.removeEventListener("click", this._onMenuClick);
      document.removeEventListener("click", this._onDocumentClick);
      document.removeEventListener("keydown", this._onKeydown);
    }

    _onButtonClick = (e) => {
      e.stopPropagation();
      this._isOpen() ? this._close() : this._open();
    };

    _onMenuClick = (e) => {
      const option = e.target.closest(this._selectors.option);
      if (!option || !this.contains(option)) return;

      const value = option.dataset.value;
      if (!value || this._select.value === value) {
        this._close();
        return;
      }

      this._select.value = value;
      this._select.dispatchEvent(new Event("change", { bubbles: true }));
      this._setActiveOption(value);
      this._close();
    };

    _onDocumentClick = (e) => {
      if (!this._isOpen() || this.contains(e.target)) return;
      this._close();
    };

    _onKeydown = (e) => {
      if (e.key === "Escape" && this._isOpen()) {
        e.preventDefault();
        this._close();
        this._button.focus();
      }
    };

    _isOpen() {
      return !this._menu.hidden;
    }

    _open() {
      this._menu.hidden = false;
      this._button.setAttribute("aria-expanded", "true");
      this.classList.add("collection-grid__sort-trigger--open");
    }

    _close() {
      this._menu.hidden = true;
      this._button.setAttribute("aria-expanded", "false");
      this.classList.remove("collection-grid__sort-trigger--open");
    }

    _setActiveOption(value) {
      this.querySelectorAll(this._selectors.option).forEach((option) => {
        const active = option.dataset.value === value;
        option.classList.toggle("collection-grid__sort-option--active", active);
        option.setAttribute("aria-selected", active ? "true" : "false");
      });
    }
  }

  customElements.define("collection-filters-layout", CollectionFiltersLayout);
  customElements.define("collection-results-count", CollectionResultsCount);
  customElements.define("collection-type-filter-badges", CollectionTypeFilterBadges);
  customElements.define("price-range-slider", PriceRangeSlider);
  customElements.define("collection-grid-sort", CollectionGridSort);
})();
